import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import logo from '../../assets/img/nova-logo-branca.png';
import { usePermissions } from '../../contexts/permissionsContext';
import useAuth from '../../hooks/useAuth';

// Icons
import AccessTimeTwoToneIcon from '@mui/icons-material/AccessTimeTwoTone';
import AccountBoxTwoToneIcon from '@mui/icons-material/AccountBoxTwoTone';
import AutoAwesomeMosaicTwoToneIcon from '@mui/icons-material/AutoAwesomeMosaicTwoTone';
import BorderAllRoundedIcon from '@mui/icons-material/BorderAllRounded';
import CoPresentTwoToneIcon from '@mui/icons-material/CoPresentTwoTone';
import ConnectedTvTwoToneIcon from '@mui/icons-material/ConnectedTvTwoTone';
import DashboardTwoToneIcon from '@mui/icons-material/DashboardTwoTone';
import ExitToAppTwoToneIcon from '@mui/icons-material/ExitToAppTwoTone';
import LaptopWindowsTwoToneIcon from '@mui/icons-material/LaptopWindowsTwoTone';
import ManageAccountsTwoToneIcon from '@mui/icons-material/ManageAccountsTwoTone';
import MonetizationOnTwoToneIcon from '@mui/icons-material/MonetizationOnTwoTone';
import SettingsApplicationsTwoToneIcon from '@mui/icons-material/SettingsApplicationsTwoTone';

const drawerWidth = 280;

export default function MobileSidebar({ open, onClose }) {
  const navigate = useNavigate();
  const { hasPermissionAction } = usePermissions();
  const { signout } = useAuth();

  const handleLinkClick = (path) => {
    navigate(path);
    onClose();
  };

  const handleLogout = () => {
    signout();
    navigate('/');
    onClose();
  };

  const handleSupport = () => {
    window.open("https://trendsuport3.glide.page", "_blank", "noopener,noreferrer");
    onClose();
  };

  return (
    <Drawer
      variant="temporary"
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 9998,
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
        },
      }}
    >
      {/* Header */}
      <Toolbar sx={{ bgcolor: '#000', color: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <img src={logo} width={'180'} height={'40'} alt="Logo" />
          <IconButton onClick={onClose} sx={{ color: '#FFF' }}>
            <ChevronLeftIcon />
          </IconButton>
        </Box>
      </Toolbar>
      
      <Divider />

      {/* Main Navigation */}
      <List sx={{ p: 0 }}>
        {hasPermissionAction("Dashboard", "isinsert") && (
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleLinkClick("/")}>
              <ListItemIcon>
                <AutoAwesomeMosaicTwoToneIcon />
              </ListItemIcon>
              <ListItemText primary="Dashboard" />
            </ListItemButton>
          </ListItem>
        )}

        {hasPermissionAction("Dashboard SCP", "isinsert") && (
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleLinkClick("/dashboardscp")}>
              <ListItemIcon>
                <DashboardTwoToneIcon />
              </ListItemIcon>
              <ListItemText primary="Dashboard SCP" />
            </ListItemButton>
          </ListItem>
        )}

        {hasPermissionAction("Cliente", "islist") && (
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleLinkClick("/listCustomer")}>
              <ListItemIcon>
                <CoPresentTwoToneIcon />
              </ListItemIcon>
              <ListItemText primary="Cliente" />
            </ListItemButton>
          </ListItem>
        )}

        {hasPermissionAction("Serviço", "islist") && (
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleLinkClick("/listService")}>
              <ListItemIcon>
                <ManageAccountsTwoToneIcon />
              </ListItemIcon>
              <ListItemText primary="Serviços" />
            </ListItemButton>
          </ListItem>
        )}

        {hasPermissionAction("Painel", "islist") && (
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleLinkClick("/listPanel")}>
              <ListItemIcon>
                <ConnectedTvTwoToneIcon />
              </ListItemIcon>
              <ListItemText primary="Painel" />
            </ListItemButton>
          </ListItem>
        )}

        {hasPermissionAction("Circuito", "islist") && (
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleLinkClick("/listCircuit")}>
              <ListItemIcon>
                <BorderAllRoundedIcon />
              </ListItemIcon>
              <ListItemText primary="Circuito" />
            </ListItemButton>
          </ListItem>
        )}
      </List>

      <Divider />

      {/* Budget and Sales */}
      <List sx={{ p: 0 }}>
        <ListItem disablePadding>
          <ListItemButton onClick={() => handleLinkClick("/orcamento")}>
            <ListItemIcon>
              <AccessTimeTwoToneIcon />
            </ListItemIcon>
            <ListItemText primary="Orçamento" />
          </ListItemButton>
        </ListItem>

        {hasPermissionAction("Vendas", "islist") && (
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleLinkClick("/listSale")}>
              <ListItemIcon>
                <MonetizationOnTwoToneIcon />
              </ListItemIcon>
              <ListItemText primary="Venda" />
            </ListItemButton>
          </ListItem>
        )}
      </List>

      <Divider />

      {/* Settings and Support */}
      <List sx={{ p: 0 }}>
        {hasPermissionAction("Usuario", "islist") && (
          <ListItem disablePadding>
            <ListItemButton onClick={() => handleLinkClick("/user")}>
              <ListItemIcon>
                <AccountBoxTwoToneIcon />
              </ListItemIcon>
              <ListItemText primary="Usuário" />
            </ListItemButton>
          </ListItem>
        )}

        {hasPermissionAction("Suporte", "isinsert") && (
          <ListItem disablePadding>
            <ListItemButton onClick={handleSupport}>
              <ListItemIcon>
                <LaptopWindowsTwoToneIcon />
              </ListItemIcon>
              <ListItemText primary="Suporte" />
            </ListItemButton>
          </ListItem>
        )}

        <Divider sx={{ my: 1 }} />

        {hasPermissionAction("Configuração", "islist") && (
          <ListItem disablePadding>
            <ListItemButton 
              onClick={() => handleLinkClick("/listConfiguration")}
              sx={{
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <SettingsApplicationsTwoToneIcon />
              </ListItemIcon>
              <ListItemText
                primary="Configurações"
                primaryTypographyProps={{
                  fontSize: '0.95rem',
                  fontWeight: 500,
                }}
              />
            </ListItemButton>
          </ListItem>
        )}

        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon>
              <ExitToAppTwoToneIcon />
            </ListItemIcon>
            <ListItemText primary="Sair" />
          </ListItemButton>
        </ListItem>
      </List>
    </Drawer>
  );
}
